import { useEffect, useRef } from 'react';
import { usePlayer } from '@/contexts/player-context';

/**
 * Episode 页面专用 hook，用于集成全局播放器
 */
export const useEpisodePlayer = (content: any, cleanReadData: any, initialTime?: number) => {
  const player = usePlayer();
  const contentIdRef = useRef<string | null>(null);
  const lastContentSignatureRef = useRef<string | null>(null);
  const cleanReadDataRef = useRef<any>(null);
  const setCurrentEpisodeRef = useRef(player.setCurrentEpisode);
  const showPlayerRef = useRef(player.showPlayer);
  const setCleanReadDataRef = useRef(player.setCleanReadData);
  
  // 更新 refs
  setCurrentEpisodeRef.current = player.setCurrentEpisode;
  showPlayerRef.current = player.showPlayer;
  setCleanReadDataRef.current = player.setCleanReadData;
  
  // 追踪是否已经设置过初始时间
  const hasSetInitialTimeRef = useRef(false);
  
  // 当内容加载完成时，设置为当前播放内容
  useEffect(() => {
    const contentId = content?.shareId || content?.content?.shareId;

    if (!content || !contentId) {
      return;
    }

    const computeSignature = (episode: any) => {
      if (!episode) {
        return 'null';
      }

      const ownerAvatar =
        episode?.ownerAvatarUrl ??
        episode?.content?.ownerAvatarUrl ??
        null;
      const audioUrl =
        episode?.audioUrl ??
        episode?.content?.audioUrl ??
        null;
      const thumbnail =
        episode?.thumbnail ??
        episode?.content?.thumbnail ??
        null;
      const title =
        episode?.title ??
        episode?.content?.title ??
        null;
      const titleTranslation =
        episode?.titleTranslation ??
        episode?.content?.titleTranslation ??
        null;
      // 🔥 添加 generationRecords 到 signature，确保当 generationRecords 变化时更新播放器
      const generationRecords =
        episode?.generationRecords ??
        episode?.content?.generationRecords ??
        null;

      return JSON.stringify({
        ownerAvatar: ownerAvatar ?? null,
        audioUrl: audioUrl ?? null,
        thumbnail: thumbnail ?? null,
        title: title ?? null,
        titleTranslation: titleTranslation ?? null,
        generationRecords: generationRecords ?? null,
      });
    };

    const newSignature = computeSignature(content);

    const hasInitialTime =
      typeof initialTime === 'number' && initialTime > 0;

    if (contentId !== contentIdRef.current) {
      contentIdRef.current = contentId;
      hasSetInitialTimeRef.current = false;

      const shouldUseInitialTime =
        hasInitialTime &&
        player.currentTime === 0 &&
        !hasSetInitialTimeRef.current;

      if (shouldUseInitialTime) {
        hasSetInitialTimeRef.current = true;
      }

      setCurrentEpisodeRef.current(
        content,
        shouldUseInitialTime ? initialTime : undefined
      );
      showPlayerRef.current();
      lastContentSignatureRef.current = newSignature;
    } else if (newSignature !== lastContentSignatureRef.current) {
      setCurrentEpisodeRef.current(content);
      lastContentSignatureRef.current = newSignature;
    }

    // 组件卸载时不清除播放器状态（保持播放）
    return () => {
      // 可选：如果需要在离开页面时暂停，可以取消注释下面的代码
      // player.pause();
    };
  }, [content]); // 移除initialTime依赖，避免重复触发
  
  // 更新 clean read 数据
  useEffect(() => {
    // 只在数据真正改变时更新
    if (cleanReadData && cleanReadData !== cleanReadDataRef.current) {
      cleanReadDataRef.current = cleanReadData;
      setCleanReadDataRef.current(cleanReadData);
    }
  }, [cleanReadData]); // 移除所有函数依赖
  
  return player;
};
